/**
 * Value formatting shared by every screen.
 *
 * The locale and currency match the React Native client exactly (en-IN, INR),
 * so the same figure reads identically on a phone and on a desktop. Formatters
 * are built once at module load rather than per render — Intl.NumberFormat is
 * expensive to construct and cheap to reuse, and a dashboard builds a lot of
 * these.
 */

const CURRENCY = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2
});

/**
 * Whole rupees, for headline figures.
 *
 * Paise on a summary tile are noise: nobody reads a dashboard to two decimal
 * places, and the extra characters make large numbers harder to compare at a
 * glance. Detail screens use the precise formatter.
 */
const CURRENCY_WHOLE = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0
});

/**
 * Formats an amount as currency.
 *
 * A missing or non-numeric value renders as an em dash rather than "₹NaN".
 * These figures come from the server, and a field that is absent — because the
 * caller's role is not allowed to see it — is a real case, not a bug to crash
 * on.
 */
export function formatCurrency(value, { whole = false } = {}) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return (whole ? CURRENCY_WHOLE : CURRENCY).format(Number(value));
}

/**
 * Short currency, for chart axis ticks.
 *
 * `compact` in en-IN produces the lakh/crore scale a reader here expects
 * (₹1.2L, ₹3.4Cr) rather than the thousand/million one. Axis ticks have very
 * little room, and a column of full-length rupee figures is what forces a chart
 * to give up a third of its width to the axis.
 */
const CURRENCY_COMPACT = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  notation: 'compact',
  maximumFractionDigits: 1
});

export function formatCompactCurrency(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return CURRENCY_COMPACT.format(Number(value));
}

/**
 * Formats a figure that can legitimately be negative, keeping the sign visible.
 *
 * Used for profit. Intl already renders a minus for negatives; this adds an
 * explicit plus for positives so a profit and a loss are told apart by their
 * first character rather than by colour alone — which is what someone who
 * cannot distinguish red from green has to rely on.
 */
export function formatSignedCurrency(value, options) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';

  const amount = Number(value);
  const formatted = formatCurrency(Math.abs(amount), options);
  if (amount > 0) return `+${formatted}`;
  if (amount < 0) return `−${formatted}`;
  return formatted;
}
